import { CardMedia, Container, Grid, Link, Tab, Tabs, Typography } from '@mui/material';
import { GridColumnOrderChangeParams, GridValidRowModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import sync from 'assets/images/sync.svg';
import { useContext, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ROUTE } from 'route/RouteEnums';
import { useAppDispatch } from 'store/hooks';
import {
  getMdacPatientsList,
  getMdacPatientsOnboardingList,
  syncProfile,
} from 'store/slices/adminSlice';
import COLORS from 'theme/colors';
import LoadingButton from '@mui/lab/LoadingButton/LoadingButton';
import { AuthContext } from 'utils/AuthContext';
import CustomDataGrid, { CustomDataGridProps } from 'utils/customDataGrid';
import {
  formatAppointmentDateTime,
  formatDate,
  formatDateTime,
  formatPhoneNumber,
  safeString,
} from 'utils/dataFormatter';
import {
  AgeFilter,
  BillingStatusFilter,
  CreditCardStatusFilter,
  CustodyTypeFilter,
  DateTimeFilterOperators,
  GenderFilter,
  InsuranceProviderFilter,
  LocationFilter,
  OrganizationTypeFilter,
  PaymentFilter,
  StateFilter,
  StatusYesNoFilter,
  UserStatusTypeFilter,
} from 'utils/gridOperators';
import { validateSession } from 'services/validateSession';

const TAB_QUERY_VALUES = ['pending', 'inprogress', 'completednoappointment', 'completed'];

function PatientOnboarding() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { user, setUserDetails } = useContext(AuthContext);
  const pageKey = 'mdac-patient-onboarding';
  const [patientData, setPatientData] = useState<any[] | null>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [completedCount, setCompletedCount] = useState(0);
  const [inprogressCount, setInprogressCount] = useState(0);
  const [completedNoAppointmentCount, setCompletedNoAppointmentCount] = useState(0);

  const handleTabChange = (event: any, newValue: any) => {
    setSelectedTab(newValue);
  };

  const handleSync = (e: any, id: any) => {
    e.stopPropagation();
    dispatch(syncProfile(id))
      .unwrap()
      .then((res: any) => {
        if (res.status === 200) {
          getMdacPatients();
        }
      });
  };
  const pendingOnboardingColumns = [
    {
      field: 'guardianFullName',
      width: 200,
      type: 'string',
      headerName: 'Guardian Name',
      hideable: false,
      sortable: true,
      filterable: true,
      valueFormatter: (params: any) => safeString(params?.value),
    },

    {
      field: 'guardianContactNumber',
      width: 140,
      type: 'phone',
      hideable: false,
      headerName: 'Contact number',
      sortable: true,
      filterable: true,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'guardianEmailAddress',
      width: 250,
      type: 'email',
      hideable: false,
      headerName: 'Email Address',
      sortable: true,
      filterable: true,
    },
    {
      field: 'preferedLocation',
      width: 110,
      type: 'string',
      hideable: false,
      headerName: 'Preferred Location',
      sortable: true,
      filterable: true,
      filterOperators: LocationFilter,
    },
    {
      field: 'guardianRegistrationDate',
      width: 150,
      type: 'date',
      headerName: 'Registration Date',
      sortable: true,
      filterable: true,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDate(params?.value),
      valueGetter: (params: any) => params?.value,
      renderCell: (params: any) => {
        return params?.value ? (
          <>
            {' '}
            {dayjs
              .utc(params?.value)
              .local()
              .format('MM/DD/YYYY')
              .substring(0, 10)}
          </>
        ) : (
          ''
        );
      },
    },

    {
      field: 'guardianStatus',
      width: 120,
      type: 'string',
      headerName: 'Status',
      sortable: true,
      filterable: true,
      filterOperators: UserStatusTypeFilter,
      renderCell: (params: any) => {
        const statusColor = getGuardianStatusColor(params?.row?.guardianStatus);
        return (
          <>
            <Typography
              sx={{
                background: '#FFFBFA',
                color: statusColor,
                border: `1px solid ${statusColor}`,
                borderRadius: '12px',
                padding: '2px 10px 0px 10px',
              }}
              textTransform='capitalize'
            >
              {params?.row?.guardianStatus}
            </Typography>
          </>
        );
      },
    },
    {
      field: 'patientInfoStatus',
      width: 100,
      type: 'string',
      headerName: 'Patient Status',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
    {
      field: 'guardianInfoStatus',
      width: 120,
      type: 'string',
      headerName: 'Guardian Status',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
    {
      field: 'childProviderInfoStatus',
      width: 114,
      type: 'string',
      headerName: 'Provider Status',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
    {
      field: 'childCounselorInfoStatus',
      width: 120,
      type: 'string',
      headerName: 'Counselor Status',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
    {
      field: 'pharmacyInfoStatus',
      width: 120,
      type: 'string',
      headerName: 'Pharmacy Status',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
    {
      field: 'paymentInfoStatus',
      width: 115,
      type: 'string',
      headerName: 'Payment Status',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
  ];

  const getGuardianStatusColor = (guardianStatus: string) => {
    if (guardianStatus === 'UNVERIFIED') {
      return '#B42318';
    }
    if (guardianStatus === 'ACTIVE') {
      return '#027A48';
    }
    if (guardianStatus === 'REGISTERED') {
      return '#1570EF';
    }
    return '#467946f8';
  };

  const columns = [
    {
      field: 'action',
      headerName: 'Action',
      filterable: false,
      sortable: false,
      hideable: false,
      disableExport: true,
      renderCell: (params: any) => {
        return (
          <>
            {params?.row?.triggerSync === 'RETRY_LIMIT_EXCEEDED' ? (
              <Link onClick={(e: any) => handleSync(e, params?.row?.patientId)}>
                <CardMedia
                  src={sync}
                  component='img'
                  sx={{ width: '24px', height: 'auto', ml: '12px' }}
                />
              </Link>
            ) : null}
          </>
        );
      },
    },
    {
      field: 'patientFullName',
      width: 200,
      type: 'string',
      headerName: 'Patient Name',
      hideable: false,
      sortable: true,
      filterable: true,
    },
    {
      field: 'guardianFullName',
      width: 200,
      type: 'string',
      headerName: 'Guardian Name',
      hideable: false,
      sortable: true,
      filterable: true,
      valueFormatter: (params: any) => safeString(params?.value),
    },

    {
      field: 'guardianContactNumber',
      width: 120,
      type: 'phone',
      hideable: false,
      headerName: 'Contact number',
      sortable: true,
      filterable: true,
      valueFormatter: (params: any) => formatPhoneNumber(params?.value),
    },
    {
      field: 'guardianEmailAddress',
      width: 210,
      type: 'email',
      hideable: false,
      headerName: 'Email Address',
      sortable: true,
      filterable: true,
    },
    {
      field: 'preferedLocation',
      width: 110,
      type: 'string',
      headerName: 'Preferred Location',
      sortable: true,
      filterable: true,
      filterOperators: LocationFilter,
    },
    {
      field: 'insurance',
      width: 100,
      type: 'string',
      headerName: 'Insurance',
      sortable: true,
      filterable: true,
      filterOperators: StatusYesNoFilter,
    },
    {
      field: 'guardianRegistrationDate',
      width: 100,
      type: 'date',
      headerName: 'Registration Date',
      sortable: true,
      filterable: true,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDate(params?.value),
      valueGetter: (params: any) => params?.value,
      renderCell: (params: any) => {
        return params?.value ? (
          <>
            {' '}
            {dayjs
              .utc(params?.value)
              .local()
              .format('MM/DD/YYYY')
              .substring(0, 10)}
          </>
        ) : (
          ''
        );
      },
    },
    {
      field: 'profileCompletionDate',
      width: 100,
      type: 'date',
      headerName: 'Profile Completion Date',
      sortable: true,
      filterable: true,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDate(params?.value),
      valueGetter: (params: any) => params?.value,
      renderCell: (params: any) => {
        return params?.value ? (
          <>
            {' '}
            {dayjs
              .utc(params?.value)
              .local()
              .format('MM/DD/YYYY')
              .substring(0, 10)}
          </>
        ) : (
          ''
        );
      },
    },
    {
      field: 'concentStatus',
      width: 100,
      type: 'date',
      headerName: 'Consents',
      sortable: true,
      filterable: false,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDate(params.value),
    },
    {
      field: 'mfshStatus',
      width: 100,
      type: 'string',
      headerName: 'MFSH',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatDate(params.value),
    },
    {
      field: 'mwiseStatus',
      width: 100,
      type: 'string',
      headerName: 'M-Wise',
      sortable: true,
      filterable: false,
      valueFormatter: (params: any) => formatDate(params.value),
    },
    {
      field: 'upcomingAppointment',
      headerName: 'Appointment Date',
      width: 180,
      type: 'dateTime',
      filterable: true,
      filterOperators: DateTimeFilterOperators,
      valueFormatter: (params: any) => formatDateTime(params?.value),
      valueGetter: (params: any) => params?.value,
      renderCell: (params: any) => {
        return params?.row?.upcomingAppointment ? (
          <>
            {dayjs.utc(params.row.upcomingAppointment).local().format('MM/DD/YYYY')}
            {' - '}
            {dayjs.utc(params.row.upcomingAppointment).local().format('hh:mm A')}
          </>
        ) : (
          'None Scheduled'
        );
      },
    },
    {
      field: 'appointmentOfficeAddress',
      width: 110,
      type: 'string',
      headerName: 'Appointment Location',
      sortable: true,
      filterable: true,
      filterOperators: LocationFilter,
    },
  ];

  const noAppointmentColumns = columns.filter(
    (column: any) => !['upcomingAppointment', 'appointmentOfficeAddress'].includes(column.field),
  );

  const getMdacPatients = async () => {
    const tab = TAB_QUERY_VALUES[selectedTab] ?? 'pending';
    const data = {
      tab: tab,
    };
    await dispatch(getMdacPatientsOnboardingList(data))
      .unwrap()
      .then((response: any) => {
        if (response?.status === 200) {
          setPatientData(response?.data?.records);
          setPendingCount(response?.data.pendingCount);
          setInprogressCount(response?.data.inprogressCount);
          setCompletedCount(response?.data.completedCount);
          setCompletedNoAppointmentCount(response?.data.completedNoAppointmentCount);
          setLoading(false);
        }
      });
  };

  useEffect(() => {
    getMdacPatients();
    window.onpopstate = () => {
      navigate(ROUTE.ADMIN_PATIENT_ONBOARDING);
    };
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch, selectedTab, user?.userId]);

  const rowSelect = async (params: any) => {
    await validateSession();
    if (selectedTab === 0) {
      return;
    }
    window.open(`${ROUTE.ADMIN_PATIENT_INFO}/${params.row.patientId}`, '_blank');
    const data = {
      ...user,
      patientId: params.row.patientId,
    };
    setUserDetails(data);
  };

  let gridColumns: any[] = columns;
  if (selectedTab === 0) {
    gridColumns = pendingOnboardingColumns;
  } else if (selectedTab === 2) {
    gridColumns = noAppointmentColumns;
  }

  let gridDashBordKey = pageKey + '_2';
  if (selectedTab === 0) {
    gridDashBordKey = pageKey + '_' + selectedTab;
  } else if (selectedTab === 2) {
    gridDashBordKey = pageKey + '_3';
  }

  return (
    <Container maxWidth='lg'>
      <Grid item xs={12}>
        <Typography
          variant='h5'
          position={'relative'}
          width={'fit-content'}
          sx={{ paddingTop: 6, textTransform: 'capitalize' }}
        >
          Onboarding Tracker List
        </Typography>
      </Grid>
      <Grid item xs={12} sx={{ backgroundColor: COLORS.White }}>
        <Grid container sx={{ background: '#FFFBF1', justifyContent: 'center' }}>
          <Grid item xs={12} mb='10px'>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant='scrollable'
              scrollButtons='auto'
              allowScrollButtonsMobile
              sx={{
                background: '#F9FAFB',
                borderRadius: '23px',
                border: '1px solid #F2F4F7',
                marginTop: '10px',
                padding: '6px 14px',
                width: 'fit-content',
                maxWidth: '100%',
                mx: 'auto',
                '.MuiTabs-flexContainer': {
                  justifyContent: 'center',
                },
                '.MuiTab-root.Mui-selected': {
                  background: '#9F84FF',
                  color: '#fff',
                  borderRadius: '23px',
                  fontWeight: 500,
                },
              }}
              TabIndicatorProps={{
                style: {
                  background: 'transparent',
                },
              }}
            >
              <Tab
                label={
                  <Grid>
                    New Registration{' '}
                    <span
                      style={{
                        background: selectedTab === 0 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {pendingCount}
                    </span>{' '}
                  </Grid>
                }
              />
              <Tab
                label={
                  <Grid>
                    In-Progress{' '}
                    <span
                      style={{
                        background: selectedTab === 1 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {inprogressCount}
                    </span>
                  </Grid>
                }
              />
              <Tab
                label={
                  <Grid>
                    Onboarded - No Appt. Scheduled{' '}
                    <span
                      style={{
                        background: selectedTab === 2 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {completedNoAppointmentCount}
                    </span>
                  </Grid>
                }
              />
              <Tab
                label={
                  <Grid>
                    Onboarded - Appt. Scheduled{' '}
                    <span
                      style={{
                        background: selectedTab === 3 ? '#644EB5' : '#F2F4F7',
                        borderRadius: '23px',
                        padding: '5px 7px',
                      }}
                    >
                      {completedCount}
                    </span>
                  </Grid>
                }
              />
            </Tabs>
          </Grid>
        </Grid>
        <CustomDataGrid
          rows={patientData ? patientData : []}
          columns={gridColumns}
          dashBordKey={gridDashBordKey}
          onRowSelect={rowSelect}
          userId={user.userId}
          setting={{ disableExport: !user?.permissions.includes('EXPORT_DATA') }}
        />
      </Grid>
    </Container>
  );
}

export default PatientOnboarding;
